import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getApprovedJobPosts } from '../services/api';

// Fake job opportunities data (fallback for old hardcoded jobs)
const jobOpportunities = [
  {
    id: 1,
    companyName: 'TechCorp Solutions',
    position: 'Senior React Developer',
    vacancies: 3,
    requiredSkills: ['React', 'JavaScript', 'Node.js', 'TypeScript'],
    location: 'Remote',
    salary: '$80k - $120k',
    description: 'We are seeking an experienced Senior React Developer to join our dynamic team. You will be responsible for building scalable web applications, collaborating with cross-functional teams, and mentoring junior developers.',
    responsibilities: [
      'Develop and maintain high-quality React applications',
      'Collaborate with designers and backend developers',
      'Write clean, maintainable, and testable code',
      'Participate in code reviews and technical discussions',
      'Mentor junior team members'
    ],
    requirements: [
      '5+ years of experience with React and modern JavaScript',
      'Strong understanding of web development fundamentals',
      'Experience with state management (Redux, Context API)',
      'Excellent problem-solving and communication skills',
      'Bachelor\'s degree in Computer Science or related field'
    ],
    benefits: [
      'Competitive salary and equity',
      'Health, dental, and vision insurance',
      'Flexible work hours and remote options',
      '401(k) with company match',
      'Professional development budget'
    ],
    type: 'Full-time',
    posted: '2 days ago'
  },
  {
    id: 2,
    companyName: 'Data Analytics Inc',
    position: 'Python Data Scientist',
    vacancies: 2,
    requiredSkills: ['Python', 'Machine Learning', 'SQL', 'TensorFlow'],
    location: 'New York',
    salary: '$90k - $140k',
    description: 'Join our data science team to build cutting-edge machine learning models that drive business decisions. Work with large datasets and implement advanced analytics solutions.',
    responsibilities: [
      'Build and deploy machine learning models',
      'Analyze large datasets to extract insights',
      'Collaborate with product teams to define metrics',
      'Present findings to stakeholders',
      'Stay current with latest ML techniques'
    ],
    requirements: [
      'MS or PhD in Computer Science, Statistics, or related field',
      '3+ years of experience in data science',
      'Proficiency in Python and ML libraries',
      'Strong statistical analysis skills',
      'Experience with cloud platforms (AWS, GCP)'
    ],
    benefits: [
      'Competitive compensation package',
      'Comprehensive health benefits',
      'Relocation assistance',
      'Conference and training budget',
      'Collaborative work environment'
    ],
    type: 'Full-time',
    posted: '1 week ago'
  },
  {
    id: 3,
    companyName: 'Cloud Systems Ltd',
    position: 'DevOps Engineer',
    vacancies: 5,
    requiredSkills: ['AWS', 'Docker', 'Kubernetes', 'CI/CD', 'Linux'],
    location: 'San Francisco',
    salary: '$100k - $150k',
    description: 'We\'re looking for a talented DevOps Engineer to help scale our infrastructure and improve deployment processes. You\'ll work with modern cloud technologies and automation tools.',
    responsibilities: [
      'Design and maintain cloud infrastructure',
      'Implement CI/CD pipelines',
      'Monitor system performance and reliability',
      'Automate operational processes',
      'Collaborate with development teams'
    ],
    requirements: [
      '4+ years of DevOps or infrastructure experience',
      'Expert knowledge of AWS services',
      'Strong experience with Docker and Kubernetes',
      'Proficiency in scripting (Bash, Python)',
      'Understanding of security best practices'
    ],
    benefits: [
      'Top-tier compensation',
      'Stock options',
      'Full health coverage',
      'Unlimited PTO',
      'Latest tech equipment'
    ],
    type: 'Full-time',
    posted: '3 days ago'
  },
  {
    id: 4,
    companyName: 'Mobile Innovations',
    position: 'Flutter Developer',
    vacancies: 4,
    requiredSkills: ['Flutter', 'Dart', 'Mobile Development', 'Firebase'],
    location: 'Austin',
    salary: '$70k - $110k',
    description: 'Join our mobile development team to create beautiful, high-performance cross-platform applications. Work on exciting projects that reach millions of users.',
    responsibilities: [
      'Develop mobile applications using Flutter',
      'Implement responsive and intuitive UI designs',
      'Integrate with backend APIs and services',
      'Optimize app performance',
      'Write unit and integration tests'
    ],
    requirements: [
      '2+ years of Flutter/Dart development',
      'Experience with mobile app architecture',
      'Knowledge of RESTful APIs',
      'Understanding of app store deployment',
      'Strong attention to detail'
    ],
    benefits: [
      'Competitive salary',
      'Health and wellness benefits',
      'Flexible schedule',
      'Remote work options',
      'Team building activities'
    ],
    type: 'Full-time',
    posted: '5 days ago'
  }
];

const JobDetailsPage = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [approvedJobs, setApprovedJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadApprovedJobs = async () => {
      const result = await getApprovedJobPosts();
      if (result.success) {
        setApprovedJobs(result.jobPosts);
      }
      setLoading(false);
    };
    loadApprovedJobs();
  }, []);

  // Find the job by ID from both approved jobs and hardcoded jobs
  // First try to find in approved jobs from localStorage
  let job = approvedJobs.find(j => j.id === jobId);

  // Fallback to hardcoded jobs for backward compatibility
  if (!job) {
    job = jobOpportunities.find(j => j.id === parseInt(jobId));
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl text-gray-600">Loading job details...</div>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-indigo-50">
        <div className="text-center bg-white p-8 rounded-xl shadow-xl">
          <div className="text-6xl mb-4">🔍</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Job Not Found</h2>
          <button
            onClick={() => navigate('/dashboard')}
            className="text-blue-600 hover:underline font-semibold"
          >
            ← Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 py-8">
      <div className="container mx-auto px-4 max-w-6xl">
        {/* Back Button */}
        <button
          onClick={() => navigate('/dashboard')}
          className="mb-6 flex items-center text-blue-600 hover:text-blue-800 font-semibold transition-colors"
        >
          ← Back to Dashboard
        </button>

        {/* Job Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl shadow-2xl p-8 mb-6 text-white">
          <div className="flex flex-col lg:flex-row items-start justify-between">
            <div className="flex-1 mb-6 lg:mb-0">
              <div className="flex items-center mb-3">
                <span className="bg-white text-blue-600 px-4 py-1 rounded-full text-sm font-bold mr-3">
                  {job.type}
                </span>
                <span className="bg-blue-500 text-white px-4 py-1 rounded-full text-sm font-semibold">
                  {job.postedDate || job.posted}
                </span>
              </div>
              <h1 className="text-4xl font-bold mb-2">{job.position}</h1>
              <p className="text-xl text-blue-100 mb-4">🏢 {job.customCompanyName || job.companyName}</p>

              <div className="flex flex-wrap gap-4 text-blue-100">
                <div className="flex items-center">
                  <span className="mr-2">📍</span>
                  <span>{job.location}</span>
                </div>
                <div className="flex items-center">
                  <span className="mr-2">💰</span>
                  <span>{job.salary}</span>
                </div>
                <div className="flex items-center">
                  <span className="mr-2">👥</span>
                  <span>{job.vacancies} {job.vacancies === 1 ? 'vacancy' : 'vacancies'}</span>
                </div>
              </div>
            </div>

            <div className="lg:ml-8">
              <a
                href={job.companyLink || "https://binarytechstation.com"}
                target="_blank"
                rel="noopener noreferrer"
                className="block bg-white text-blue-600 px-8 py-4 rounded-xl font-bold text-lg hover:bg-blue-50 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 text-center whitespace-nowrap"
              >
                🚀 Apply Now
              </a>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Job Description */}
            <div className="bg-white rounded-xl shadow-xl p-6 border-l-4 border-blue-500">
              <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                <span className="bg-blue-500 text-white p-2 rounded-lg mr-3">📝</span>
                Job Description
              </h2>
              <p className="text-gray-700 leading-relaxed text-lg">{job.description}</p>
            </div>

            {/* Responsibilities */}
            <div className="bg-white rounded-xl shadow-xl p-6 border-l-4 border-green-500">
              <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                <span className="bg-green-500 text-white p-2 rounded-lg mr-3">✓</span>
                Responsibilities
              </h2>
              <ul className="space-y-3">
                {job.responsibilities.map((item, index) => (
                  <li key={index} className="flex items-start text-gray-700">
                    <span className="text-green-500 mr-3 text-xl flex-shrink-0">•</span>
                    <span className="leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Requirements */}
            <div className="bg-white rounded-xl shadow-xl p-6 border-l-4 border-purple-500">
              <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                <span className="bg-purple-500 text-white p-2 rounded-lg mr-3">🎯</span>
                Requirements
              </h2>
              <ul className="space-y-3">
                {job.requirements.map((item, index) => (
                  <li key={index} className="flex items-start text-gray-700">
                    <span className="text-purple-500 mr-3 text-xl flex-shrink-0">•</span>
                    <span className="leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Benefits */}
            <div className="bg-white rounded-xl shadow-xl p-6 border-l-4 border-yellow-500">
              <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                <span className="bg-yellow-500 text-white p-2 rounded-lg mr-3">🎁</span>
                Benefits & Perks
              </h2>
              <ul className="space-y-3">
                {job.benefits.map((item, index) => (
                  <li key={index} className="flex items-start text-gray-700">
                    <span className="text-yellow-500 mr-3 text-xl flex-shrink-0">•</span>
                    <span className="leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Required Skills */}
            <div className="bg-white rounded-xl shadow-xl p-6 border-t-4 border-blue-500 sticky top-6">
              <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                <span className="text-2xl mr-2">💡</span>
                Required Skills
              </h3>
              <div className="flex flex-wrap gap-2 mb-6">
                {job.requiredSkills.map((skill, index) => (
                  <span
                    key={index}
                    className="bg-gradient-to-r from-blue-100 to-indigo-100 text-blue-800 px-4 py-2 rounded-full text-sm font-semibold shadow-sm"
                  >
                    {skill}
                  </span>
                ))}
              </div>

              {/* Apply Button */}
              <a
                href="https://binarytechstation.com"
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full bg-gradient-to-r from-green-600 to-emerald-600 text-white py-4 rounded-xl font-bold hover:from-green-700 hover:to-emerald-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 text-center mb-3"
              >
                🚀 Apply for this Job
              </a>

              <Link
                to="/dashboard"
                className="block w-full bg-gradient-to-r from-gray-400 to-gray-500 text-white py-4 rounded-xl font-bold hover:from-gray-500 hover:to-gray-600 transition-all duration-300 shadow-lg hover:shadow-xl text-center"
              >
                ← Back to Dashboard
              </Link>
            </div>

            {/* Company Info */}
            <div className="bg-white rounded-xl shadow-xl p-6 border-t-4 border-indigo-500">
              <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                <span className="text-2xl mr-2">🏢</span>
                About Company
              </h3>
              <div className="space-y-3">
                <div className="bg-indigo-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-600 font-semibold mb-1">Company</p>
                  <p className="text-gray-800 font-medium">{job.companyName}</p>
                </div>
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-600 font-semibold mb-1">Location</p>
                  <p className="text-gray-800 font-medium">{job.location}</p>
                </div>
                <div className="bg-green-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-600 font-semibold mb-1">Employment Type</p>
                  <p className="text-gray-800 font-medium">{job.type}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobDetailsPage;
